import React, { Fragment, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { getThumbBySize } from '../../../util/photo';
import { IMAGE_SIZE_CODE } from '../../../constants/image-size-codes';
import PinchZoomPan from '../PinchZoomPan';
import Button from '../Button';
import useComponentSize from '../../../hooks/useComponentSize';
import DismissiblePersistent from '../DismissiblePersistent';
import { WG_WALDOGRAM_IMAGE_TIP } from '../../../constants/dismissibleKeys';
import Alert from '../Alert';
import { isMobile } from '../../../util/device-detector';
import { getInitialPhotoCropbox } from '../../../services/cards/components/CardElementPhoto/helpers';
import useDebounce from '../../../hooks/useDebounce';

const propTypes = {
  className: PropTypes.string,
  onPhotoSelect: PropTypes.func.isRequired,
  onPhotoCropboxSet: PropTypes.func.isRequired,
  photo: PropTypes.shape({
    id: PropTypes.string.isRequired,
    thumbnail_urls: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
      }),
    ),
  }),
};

const WaldogramMainPhotoSelect = ({
  onPhotoSelect,
  onPhotoCropboxSet,
  photo,
  cropbox,
  className,
}) => {
  const [localCropbox, setLocalCropbox] = useState(getInitialPhotoCropbox());
  const [debouncedCropbox, cancelCropbox] = useDebounce(localCropbox, 300);
  const photoWrapRef = useRef(null);
  const containerSize = useComponentSize(photoWrapRef);
  const baseClassName = 'waldogram-main-photo-select';
  const classes = classNames({
    [baseClassName]: true,
    [className]: !!className,
  });

  useEffect(() => {
    if (cropbox) {
      setLocalCropbox(cropbox);
    }
  }, [cropbox]);

  useEffect(() => {
    if (debouncedCropbox) {
      onPhotoCropboxSet(0, debouncedCropbox);
    }

    return () => {
      cancelCropbox?.();
    };
  }, [debouncedCropbox]);

  const handlePhotoSelect = event => {
    event.preventDefault();

    onPhotoSelect({
      position: 0,
      photo,
    });
  };

  return (
    <div className={classes} ref={photoWrapRef}>
      {!photo && (
        <div className={`${baseClassName}-placeholder`}>
          <span className={`${baseClassName}-title`}>
            Personalize your postcard
          </span>

          <span className={`${baseClassName}-description`}>
            Select the main photo for this postcard.
          </span>

          <div className={`${baseClassName}-picture-select-buttons`}>
            <Button
              className="waldogram-photo-select-btn btn-xs"
              onClick={handlePhotoSelect}
            >
              <Fragment>
                <span>Select a photo&hellip;</span>
              </Fragment>
            </Button>
          </div>
        </div>
      )}

      {photo && (
        <div className={`${baseClassName}-photo-wrap`} key={photo.id}>
          <PinchZoomPan
            key={photo.id}
            maxScale={10}
            initialLeft={
              localCropbox.x ? localCropbox.x * containerSize.width : 0
            }
            initialTop={
              localCropbox.y ? localCropbox.y * containerSize.height : 0
            }
            initialScale={localCropbox.scale || 1}
            initialRotation={localCropbox.rotation || 0}
            onChange={({ x, y, scale, rotation }) => {
              setLocalCropbox({
                x: x / containerSize.width,
                y: y / containerSize.height,
                scale,
                rotation,
              });
            }}
          >
            <img
              src={
                getThumbBySize(photo.thumbnail_urls, IMAGE_SIZE_CODE.LARGE).url
              }
              className={`${baseClassName}-photo-img`}
              alt=""
            />
          </PinchZoomPan>

          <div className="waldogram-edit-buttons-centered">
            <Button
              className="btn-xs btn-edit-photo-selection"
              onClick={handlePhotoSelect}
            >
              Change photo
            </Button>
          </div>
        </div>
      )}

      {photo && (
        <DismissiblePersistent storeKey={WG_WALDOGRAM_IMAGE_TIP}>
          {onDismiss => (
            <Alert
              type="info"
              className="waldogram-image-tip"
              onDismiss={onDismiss}
            >
              {isMobile()
                ? 'Pinch to zoom and drag with your finger to position the image.'
                : 'Scroll to zoom and move the image around to position it.'}
            </Alert>
          )}
        </DismissiblePersistent>
      )}
    </div>
  );
};

WaldogramMainPhotoSelect.propTypes = propTypes;

export default WaldogramMainPhotoSelect;
